import type { LngLatBounds } from '@yandex/ymaps3-types';
import { LngLat } from '@yandex/ymaps3-types';
import { YMapLocationRequest } from '@yandex/ymaps3-types/imperative/YMap';
import { Feature as ClustererFeature } from '@yandex/ymaps3-types/packages/clusterer';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Shop } from '@/api/backend/contract/Shop';
import {
  CITY_CENTER_BOUNDS_PADDING,
  COMMON_LOCATION_PARAMS,
  DEFAULT_CENTER_VALUE,
  DEFAULT_SELECT_POINT_ZOOM_VALUE,
  MAX_MAP_ZOOM,
  MIN_MAP_ZOOM,
  NEUTRAL_MAP_ZOOM
} from '@/util/map-point-maps/consts';
import { PointFeature } from '@/util/map-point-maps/types';
import { getBounds } from '@/util/map-point-maps/utils/getBounds';

function getCenterBounds(center: LngLat, padding: number): LngLatBounds {
  const [lng, lat] = center;
  return [
    [lng - padding, lat - padding],
    [lng + padding, lat + padding]
  ] as LngLatBounds;
}

const isValidLngLat = (coords?: LngLat): coords is LngLat =>
  coords !== undefined && coords.every((value) => Number.isFinite(value) && value !== 0);

const isExplicitCenter = (center?: LngLat): center is LngLat => isValidLngLat(center);

const resolveCenter = (center?: LngLat): LngLat =>
  isExplicitCenter(center) ? center : DEFAULT_CENTER_VALUE;

/**
 * Хук для управления состоянием карты
 */
export function useMapController(center?: LngLat) {
  const hasExplicitCenter = useMemo(() => isExplicitCenter(center), [center]);

  const [location, setLocation] = useState<YMapLocationRequest>(() => ({
    zoom: NEUTRAL_MAP_ZOOM,
    center: resolveCenter(center)
  }));

  const [selectedPoint, setSelectedPoint] = useState<PointFeature | Shop | null>(null);

  useEffect(() => {
    if (!hasExplicitCenter || !center) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLocation((prevLocation) => ({
      ...prevLocation,
      center,
      bounds: getCenterBounds(center, CITY_CENTER_BOUNDS_PADDING)
    }));
  }, [center, hasExplicitCenter]);

  /**
   * Обработчик клика по маркеру
   */
  const handleMarkerClick = useCallback(
    (isMap: boolean) => (feature: PointFeature | Shop | null) => {
      if (feature) {
        const markerCenter: LngLat = [
          feature.geometry.coordinates[0],
          feature.geometry.coordinates[1]
        ];

        if (isValidLngLat(markerCenter)) {
          setLocation((prevLocation) => ({
            ...prevLocation,
            bounds: undefined,
            center: markerCenter,
            zoom: DEFAULT_SELECT_POINT_ZOOM_VALUE,
            ...COMMON_LOCATION_PARAMS
          }));
        }
      }
      setSelectedPoint(feature);

      return { feature, isMap };
    },
    []
  );

  /**
   * Обработчик клика по кластеру
   */
  const handleClusterClick = useCallback((features: ClustererFeature[]) => {
    const bounds = getBounds(
      features.map((feature: ClustererFeature) => [
        feature.geometry.coordinates[0],
        feature.geometry.coordinates[1]
      ])
    );

    if (!bounds) return;

    setLocation((prevLocation) => ({
      ...prevLocation,
      bounds,
      ...COMMON_LOCATION_PARAMS
    }));
  }, []);

  /**
   * Обработчик выбора геолокации из поиска
   */
  const handleLocationSelect = useCallback((coordinates: [number, number]) => {
    if (!isValidLngLat(coordinates)) return;

    setLocation((prevLocation) => ({
      ...prevLocation,
      bounds: undefined,
      center: coordinates,
      zoom: DEFAULT_SELECT_POINT_ZOOM_VALUE,
      ...COMMON_LOCATION_PARAMS
    }));
  }, []);

  const handleZoomChange = useCallback((delta: 1 | -1, currentCenter: LngLat, currentZoom: number) => {
    const nextZoom = currentZoom + delta;

    if (nextZoom < MIN_MAP_ZOOM || nextZoom > MAX_MAP_ZOOM) {
      return;
    }

    setLocation({
      bounds: undefined,
      center: currentCenter,
      zoom: nextZoom,
      ...COMMON_LOCATION_PARAMS
    });
  }, []);

  const handleZoomIn = useCallback(
    (currentCenter: LngLat, currentZoom: number) => handleZoomChange(1, currentCenter, currentZoom),
    [handleZoomChange]
  );
  const handleZoomOut = useCallback(
    (currentCenter: LngLat, currentZoom: number) => handleZoomChange(-1, currentCenter, currentZoom),
    [handleZoomChange]
  );

  return {
    location,
    selectedPoint,
    setLocation,
    setSelectedPoint,
    handleMarkerClick,
    handleClusterClick,
    handleLocationSelect,
    handleZoomIn,
    handleZoomOut
  };
}
